
import React, { useState } from "react";
import happy_pharmacist from "../assets/happyPharmacist.jpg";
import { FaShieldAlt, FaTools, FaHeadset, FaListAlt } from "react-icons/fa";

const WhyUs = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  
  const features = [
    {
      id: 0,
      icon: <FaHeadset className="text-4xl text-blue-600" />,
      title: "Priority Customer Support",
      description: "We value your time and trust, which is why we offer Priority Customer Support to ensure you get the assistance you need, whenever you need it. With Priority Support, you're never left waiting. We're here to keep your pharmacy running smoothly."
    },
    {
      id: 1,
      icon: <FaTools className="text-4xl text-blue-600" />,
      title: "Customizable Solutions",
      description: "We know that every pharmacy is unique. That's why our system offers customizable features that adapt to your specific requirements, ensuring a personalized experience for you and your team. You're not just getting a one-size-fits-all solution; you're getting a system designed to grow with you."
    },
    {
      id: 2,
      icon: <FaListAlt className="text-4xl text-blue-600" />,
      title: "Comprehensive Features",
      description: "Get a full suite of tools designed to streamline every aspect of your pharmacy operations. Whether you're managing prescriptions, inventory, or customer relationships, our system has you covered with intuitive, powerful features."
    },
    {
      id: 3,
      icon: <FaShieldAlt className="text-4xl text-blue-600" />,
      title: "Data Security",
      description: "We understand the critical importance of protecting your pharmacy's sensitive information. That's why we've built our system with top-tier security measures to ensure your data is safe at all times. Your trust is our priority."
    }
  ];

  return (
    <div className="py-24 bg-gradient-to-br from-white to-blue-50" id="whyus">
      <div className="container mx-auto px-5">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center py-2 px-4 bg-blue-50 border border-blue-100 rounded-full shadow-sm mb-4">
            <div className="h-2 w-2 rounded-full bg-blue-500 mr-2"></div>
            <p className="text-sm font-medium text-gray-600">Why Choose Us</p>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Excellence in <span className="text-blue-600 relative">
              Pharmacy
              <span className="absolute -bottom-2 left-0 h-1 w-full bg-blue-400 opacity-50 rounded-full"></span>
            </span> Management
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Column: Feature Tabs */}
          <div className="order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8">
              {/* Tabs Navigation */}
              <div className="flex flex-wrap gap-2 mb-8 border-b">
                {features.map((feature, index) => (
                  <button
                    key={feature.id}
                    onClick={() => setActiveFeature(index)}
                    className={`relative px-4 py-3 transition-all duration-300 ${
                      activeFeature === index
                        ? "text-blue-600 font-medium"
                        : "text-gray-500 hover:text-blue-500"
                    }`}
                  >
                    {feature.title}
                    {activeFeature === index && (
                      <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500 rounded-full"></span>
                    )}
                  </button>
                ))}
              </div>

              {/* Active Feature Content */}
              <div className="min-h-64">
                {features.map((feature, index) => (
                  <div
                    key={feature.id}
                    className={`transition-all duration-500 ${
                      activeFeature === index 
                        ? "opacity-100 transform translate-y-0" 
                        : "opacity-0 absolute -z-10 transform translate-y-4"
                    }`}
                  >
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                    
                    {/* Feature highlights */}
                    <div className="grid grid-cols-2 gap-4 mt-8">
                      {activeFeature === 0 && (
                        <>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">24/7 Support Access</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Dedicated Account Manager</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Priority Issue Resolution</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Regular Check-ins</span>
                          </div>
                        </>
                      )}
                      
                      {activeFeature === 1 && (
                        <>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Custom Dashboard</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Workflow Automation</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Branding Options</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">API Integration</span>
                          </div>
                        </>
                      )}
                      
                      {activeFeature === 2 && (
                        <>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Inventory Management</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">E-Prescriptions</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Patient Management</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Financial Reporting</span>
                          </div>
                        </>
                      )}
                      
                      {activeFeature === 3 && (
                        <>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">HIPAA Compliant</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Encryption</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Access Controls</span>
                          </div>
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-700">Secure Backups</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Right Column: Image */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-105">
              {/* Blue gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-blue-900/40 z-10"></div>
              
              <img 
                src={happy_pharmacist} 
                alt="Happy Pharmacist using RxUltimate" 
                className="w-full h-full object-cover"
              />
              
              {/* Floating stat cards */}
              <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg z-20">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <span className="text-green-500 font-bold">↑</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-xl font-bold text-gray-800">95%</p>
                    <p className="text-xs text-gray-600">Customer Satisfaction</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg z-20">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-500 font-bold">↓</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-xl font-bold text-gray-800">30%</p>
                    <p className="text-xs text-gray-600">Reduced Admin Time</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-blue-100 rounded-full opacity-50 blur-2xl -z-10"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-teal-100 rounded-full opacity-50 blur-xl -z-10"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-28 h-28 border-8 border-blue-100 rounded-full -z-10"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyUs;